/**
 * Pure validation predicates for the SSO settings form.
 *
 * Mirrors the server-side rules in OrganizationSSOInputs.ts (class-validator)
 * and OrganizationService.updateSSOConfig (business rules). Client validation
 * is for UX — the server remains authoritative.
 */

import type { SSOFormState, SSOFormErrors } from "../types";

// RFC 4122 v4 UUID — same shape Microsoft Entra ID emits for tenant IDs.
const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// RFC 1123 hostname — labels of [a-z0-9-], 1–63 chars, total ≤ 253. Punycode
// (`xn--*`) is permitted; raw umlauts are not (callers should punycode first).
const RFC1123_DOMAIN_RE =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

// Microsoft personal-accounts tenant — never accept (Phase 1).
const MSA_TENANT_ID = "9188040d-6c67-4c5b-b112-36a304b66dad";

export function isValidTenantId(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  if (!UUID_V4_RE.test(trimmed)) return false;
  if (trimmed === MSA_TENANT_ID) return false;
  return true;
}

export function isValidDomain(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return RFC1123_DOMAIN_RE.test(trimmed);
}

/**
 * Mirror of OrganizationService.updateSSOConfig() rules 1–4. Returned shape is
 * keyed so the form can render errors next to the offending control.
 */
export function validateSSOForm(state: SSOFormState): SSOFormErrors {
  const errors: SSOFormErrors = {};

  // Field-level validators
  if (state.microsoft.enabled) {
    if (!state.microsoft.tenantId.trim()) {
      errors.microsoftTenantId = "tenantIdRequired";
    } else if (!isValidTenantId(state.microsoft.tenantId)) {
      errors.microsoftTenantId = "tenantIdInvalid";
    }
  }

  if (state.google.enabled) {
    if (!state.google.hostedDomain.trim()) {
      errors.googleHostedDomain = "hostedDomainRequired";
    } else if (!isValidDomain(state.google.hostedDomain)) {
      errors.googleHostedDomain = "hostedDomainInvalid";
    }
  }

  const invalidDomain = state.emailDomains.find((d) => !isValidDomain(d));
  if (invalidDomain) {
    errors.emailDomains = "domainInvalid";
  }

  // Cross-field business rules
  if (state.enforced && !state.enabled) {
    errors.form = "enforceRequiresEnabled";
  } else if (
    state.enabled &&
    !state.microsoft.enabled &&
    !state.google.enabled
  ) {
    errors.form = "atLeastOneProvider";
  } else if (state.enforced && state.emailDomains.length === 0) {
    errors.form = "enforceRequiresDomain";
  }

  return errors;
}

export function hasErrors(errors: SSOFormErrors): boolean {
  return Object.keys(errors).length > 0;
}
